import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
export function SporeMyceliumHero() {
  // Generate static paths and initial particle positions to avoid hydration mismatches
  const {
    spores,
    threads
  } = useMemo(() => {
    // Generate Spores
    const generatedSpores = Array.from({
      length: 60
    }, (_, i) => {
      const size = Math.random() * 4 + 1; // 1px to 5px
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      const duration = 15 + Math.random() * 20; // 15s to 35s
      const delay = Math.random() * -20; // Negative delay so they start already moving
      // Assign colors based on brand palette
      const colors = ['#78e1c4', '#4DE7D4', '#edcd99', '#a0e6e9'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      // Layering: smaller = slower, lower opacity
      const opacity = size < 2 ? 0.15 : size < 3.5 ? 0.3 : 0.5;
      return {
        id: i,
        size,
        startX,
        startY,
        duration,
        delay,
        color,
        opacity,
        // Movement targets
        endY: startY - 20 - Math.random() * 30,
        xDrift: (Math.random() - 0.5) * 20 // Drift left/right
      };
    });
    // Generate Mycelium Threads
    // We'll create a branching structure originating from a few points
    const generateBranch = (startX: number, startY: number, angle: number, length: number, depth: number, maxDepth: number): string => {
      if (depth >= maxDepth || length < 5) return '';
      const endX = startX + Math.cos(angle) * length;
      const endY = startY + Math.sin(angle) * length;
      // Subtle curve
      const controlX = startX + Math.cos(angle) * length * 0.5 + (Math.random() - 0.5) * 10;
      const controlY = startY + Math.sin(angle) * length * 0.5 + (Math.random() - 0.5) * 10;
      let path = `M ${startX},${startY} Q ${controlX},${controlY} ${endX},${endY}`;
      const numBranches = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < numBranches; i++) {
        const branchAngle = angle + (Math.random() - 0.5) * Math.PI * 0.8;
        const branchLength = length * (0.5 + Math.random() * 0.4);
        path += ' ' + generateBranch(endX, endY, branchAngle, branchLength, depth + 1, maxDepth);
      }
      return path;
    };
    const generatedThreads = Array.from({
      length: 12
    }, (_, i) => {
      // Start from edges or bottom
      const startX = Math.random() * 100;
      const startY = 80 + Math.random() * 40; // mostly bottom half
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI; // pointing generally upwards
      const length = 20 + Math.random() * 30;
      return {
        id: i,
        path: generateBranch(startX, startY, angle, length, 0, 4),
        duration: 8 + Math.random() * 10,
        delay: Math.random() * 5,
        opacity: 0.1 + Math.random() * 0.15
      };
    });
    return {
      spores: generatedSpores,
      threads: generatedThreads
    };
  }, []);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-232">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-233">
        <defs data-id="element-234">
          <filter id="spore-glow" data-id="element-235">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur" data-id="element-236" />
            <feMerge data-id="element-237">
              <feMergeNode in="coloredBlur" data-id="element-238" />
              <feMergeNode in="SourceGraphic" data-id="element-239" />
            </feMerge>
          </filter>
        </defs>

        {/* Mycelium Threads Layer */}
        <g stroke="#78e1c4" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-240">
          {threads.map(thread => <motion.path key={`thread-${thread.id}`} d={thread.path} strokeWidth="0.15" initial={{
          opacity: 0,
          pathLength: 0
        }} animate={{
          opacity: [0, thread.opacity, thread.opacity * 0.5, thread.opacity],
          pathLength: 1
        }} transition={{
          opacity: {
            duration: thread.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: thread.delay
          },
          pathLength: {
            duration: 4,
            ease: 'easeOut'
          }
        }} data-id="element-241" />)}
        </g>

        {/* Floating Spores Layer */}
        {spores.map(spore => <motion.circle key={`spore-${spore.id}`} r={spore.size * 0.1} // scale down for viewBox 0-100
      fill={spore.color} filter="url(#spore-glow)" initial={{
        cx: spore.startX,
        cy: spore.startY,
        opacity: 0
      }} animate={{
        cx: spore.startX + spore.xDrift,
        cy: spore.endY,
        opacity: [0, spore.opacity, spore.opacity, 0]
      }} transition={{
        duration: spore.duration,
        repeat: Infinity,
        delay: spore.delay,
        ease: 'linear'
      }} data-id="element-242" />)}
      </svg>

      {/* Subtle depth overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(14,37,51,0.4)_100%)]" data-id="element-243" />
    </div>;
}